import React, { useEffect, useState } from "react";
import { Link } from 'react-router-dom';

import { FaWhatsapp, FaTimes, FaChevronLeft, FaChevronRight, FaDownload, FaShareAlt, FaLock, FaMinus, FaPlus } from "react-icons/fa";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import ProductCard from "../shared/ProductCard";
import { Modal } from "react-bootstrap";
import { useProducts } from "../ProductContext";
import { isVideoUrl } from "../cloudinaryConfig";
import { useAuth } from "../AuthContext";

// Amazon-style Hover Zoom Component (Desktop only)
const AmazonZoom = ({ src, alt, onOpenModal }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState('0% 0%');
  const containerRef = React.useRef(null);

  const LENS_SIZE = 150;
  const ZOOM_LEVEL = 2.5;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 992);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const lensX = Math.max(LENS_SIZE / 2, Math.min(x, rect.width - LENS_SIZE / 2));
    const lensY = Math.max(LENS_SIZE / 2, Math.min(y, rect.height - LENS_SIZE / 2));
    setLensPosition({ x: lensX - LENS_SIZE / 2, y: lensY - LENS_SIZE / 2 });
    const bgX = (x / rect.width) * 100;
    const bgY = (y / rect.height) * 100;
    setBackgroundPosition(`${bgX}% ${bgY}%`);
  };

  const handleMouseEnter = () => { if (!isMobile) setIsHovering(true); };
  const handleMouseLeave = () => { setIsHovering(false); };
  const handleClick = () => { if (isMobile && onOpenModal) onOpenModal(); };

  return (
    <div className="amazon-zoom-wrapper w-100">
      <div
        ref={containerRef}
        className="amazon-zoom-main"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{ cursor: isMobile ? 'zoom-in' : 'crosshair' }}
      >
        <img src={src} alt={alt} className="amazon-zoom-img" draggable={false} />

        {isHovering && !isMobile && (
          <div
            className="amazon-zoom-lens"
            style={{ left: lensPosition.x, top: lensPosition.y, width: LENS_SIZE, height: LENS_SIZE }}
          />
        )}

        <div
          className="amazon-zoom-hint d-none d-lg-block"
          onClick={(e) => { e.stopPropagation(); if (onOpenModal) onOpenModal(); }}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </div>
      </div>

      {isHovering && !isMobile && (
        <div
          className="amazon-zoom-preview"
          style={{
            backgroundImage: `url(${src})`,
            backgroundSize: `${ZOOM_LEVEL * 100}%`,
            backgroundPosition: backgroundPosition,
          }}
        />
      )}
    </div>
  );
};

const ZoomableImage = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState({ x: "50%", y: "50%" });

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    const { left, top, width, height } = e.target.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setPosition({ x: `${x}%`, y: `${y}%` });
  };

  const handleClick = () => {
    setIsZoomed(!isZoomed);
    if (isZoomed) setPosition({ x: "50%", y: "50%" });
  };

  return (
    <div
      className="zoom-container"
      style={{ overflow: "hidden", cursor: isZoomed ? "zoom-out" : "zoom-in", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <img
        src={src}
        alt={alt}
        className="gallery-main-img-dark"
        style={{
          transform: isZoomed ? "scale(2)" : "scale(1)",
          transformOrigin: `${position.x} ${position.y}`,
          transition: "transform 0.2s ease-out",
          width: "100%",
          height: "100%",
          maxHeight: "85vh",
          objectFit: "contain"
        }}
        draggable={false}
      />
    </div>
  );
};

export default function ProductDetails({ productId }) {
  const [product, setProduct] = useState(null);
  const [mainIdx, setMainIdx] = useState(0);
  const [related, setRelated] = useState([]);
  const { getProductDetailsCache, setProductDetailsData } = useProducts();
  const { isLoggedIn, isApproved, userProfile } = useAuth();
  const [quantity, setQuantity] = useState(1);

  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [modalMainIdx, setModalMainIdx] = useState(0);

  useEffect(() => {
    if (!productId) return;
    setMainIdx(0);

    const ref = doc(db, "products", productId);
    getDoc(ref)
      .then((snap) => {
        if (snap.exists()) {
          const productData = { id: snap.id, ...snap.data() };
          setProduct(productData);

          const cached = getProductDetailsCache(productId);
          if (cached && cached.related && cached.related.length > 0) {
            setRelated(cached.related);
            setProductDetailsData(productId, { product: productData, related: cached.related });
            return;
          }

          if (productData.category) {
            const col = collection(db, "products");
            const q = query(col, where("category", "==", productData.category));
            getDocs(q)
              .then((relSnap) => {
                const arr = relSnap.docs
                  .map((d) => {
                    const data = d.data();
                    const images = data.images ?? data.image;
                    const firstImage = Array.isArray(images) ? images[0] : images;
                    return { id: d.id, ...data, images: firstImage ? [firstImage] : [] };
                  })
                  .filter((p) => p.id !== productData.id);
                const relatedProducts = arr.slice(0, 4);
                setRelated(relatedProducts);
                setProductDetailsData(productId, { product: productData, related: relatedProducts });
              })
              .catch(console.error);
          } else {
            setProductDetailsData(productId, { product: productData, related: [] });
          }
        }
      })
      .catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  if (!product) {
    return (
      <div className="pd-page">
        <div className="skeleton skeleton-breadcrumbs mb-3"></div>
        <div className="pd-layout">
          <div className="pd-gallery-col">
            <div className="skeleton" style={{ height: 420, borderRadius: 20 }}></div>
          </div>
          <div className="pd-info-col">
            <div className="skeleton skeleton-title-sm mb-2"></div>
            <div className="skeleton skeleton-title-lg mb-3"></div>
            <div className="skeleton skeleton-price mb-3"></div>
            <div className="skeleton skeleton-text-block"></div>
          </div>
        </div>
      </div>
    );
  }

  const imagesRaw = product?.images ?? product?.image ?? [];
  const images = Array.isArray(imagesRaw) ? imagesRaw : imagesRaw ? [imagesRaw] : [];
  const mainSrc = images.length > 0 ? images[mainIdx] : null;

  const handlePrevImage = (e) => {
    e.stopPropagation();
    setMainIdx((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    setMainIdx((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const MAX_THUMBS = 4;
  const showMoreOverlay = images.length > MAX_THUMBS;
  const displayedThumbs = images.slice(0, MAX_THUMBS);
  const remainingCount = images.length - MAX_THUMBS + 1;

  const handleThumbClick = (index) => {
    if (showMoreOverlay && index === MAX_THUMBS - 1) {
      setModalMainIdx(0);
      setShowGalleryModal(true);
    } else {
      setMainIdx(index);
    }
  };

  const openModal = (startIdx = 0) => {
    setModalMainIdx(startIdx);
    setShowGalleryModal(true);
  };

  const nextModalImage = () => setModalMainIdx((prev) => (prev + 1) % images.length);
  const prevModalImage = () => setModalMainIdx((prev) => (prev - 1 + images.length) % images.length);


  const handleWhatsAppInquiry = () => {
    if (isApproved && quantity > 0) {
      const price = product.price;
      const offer = product.offer || 0;
      const discountedPrice = Math.round(price * (1 - offer / 100));
      const totalPrice = discountedPrice * quantity;
      const text = `*New Order Enquiry*\n\nProduct: *${product.name}*\nSKU: ${product.sku || 'N/A'}\nQuantity: ${quantity}\nUnit Price: ₹${discountedPrice}\nTotal: ₹${totalPrice}\n\nCustomer: ${userProfile?.name || 'N/A'} (${userProfile?.company || 'N/A'})\nPhone: ${userProfile?.phone || 'N/A'}\nCity: ${userProfile?.city || 'N/A'}\n\nLink: ${window.location.href}`;
      const url = `https://wa.me/919924382499?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    } else {
      const text = `Hello, I am interested in *${product.name}*.\nLink: ${window.location.href}`;
      const url = `https://wa.me/919924382499?text=${encodeURIComponent(text)}`;
      window.open(url, '_blank');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, text: `Check out ${product.name} on Shree Kamdhenu Furniture!`, url: window.location.href });
      } catch (error) { console.log('Error sharing', error); }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="pd-page">

      {/* Breadcrumbs */}
      <nav className="pd-breadcrumb" aria-label="breadcrumb">
        <ul className="pd-breadcrumb-list mb-2 mb-sm-3">
          <li><Link to="/" className="pd-bc-link">Home</Link></li>
          <li className="pd-bc-sep" aria-hidden="true">›</li>
          <li><Link to={`/collections?category=${encodeURIComponent(product.category)}`} className="pd-bc-link">{product.category}</Link></li>
          <li className="pd-bc-sep" aria-hidden="true">›</li>
          <li className="pd-bc-current">{product.name}</li>
        </ul>
      </nav>

      {/* Main 2-column layout */}
      <div className="pd-layout">

        {/* ── LEFT: Gallery ── */}
        <div className="pd-gallery-col">

          {/* Desktop: Thumbnails on left + Main image on right */}
          <div className="pd-gallery-inner">

            {/* Vertical thumbs (hidden on mobile) */}
            {images.length > 1 && (
              <div className="pd-thumbs-col">
                {displayedThumbs.map((src, i) => {
                  const isOverlay = showMoreOverlay && i === MAX_THUMBS - 1;
                  const isVid = isVideoUrl(src);
                  return (
                    <div
                      key={i}
                      className={`pd-thumb ${i === mainIdx && !isOverlay ? 'active' : ''}`}
                      onClick={() => handleThumbClick(i)}
                    >
                      {isVid
                        ? <video src={src} muted style={{ width: '100%', height: '100%', objectFit: 'cover', pointerEvents: 'none' }} />
                        : <img src={src} alt={`View ${i + 1}`} style={{ pointerEvents: 'none' }} />
                      }
                      {isOverlay && <div className="pd-thumb-overlay"><span>+{remainingCount}</span></div>}
                      {isVid && !isOverlay && <div className="pd-video-badge">▶</div>}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Main image area */}
            <div className="pd-main-image-wrap">
              {mainSrc ? (
                isVideoUrl(mainSrc)
                  ? (
                    <div className="pd-main-frame">
                      <video key={mainSrc} src={mainSrc} controls autoPlay muted loop playsInline style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                    </div>
                  )
                  : (
                    <AmazonZoom key={mainSrc} src={mainSrc} alt={product.name} onOpenModal={() => openModal(mainIdx)} />
                  )
              ) : (
                <div className="pd-main-frame">
                  <div className="pd-no-image">No Image Available</div>
                </div>
              )}

              {/* Mobile slider nav buttons */}
              {images.length > 1 && (
                <>
                  <button className="pd-slider-btn pd-slider-prev" onClick={handlePrevImage} aria-label="Previous Image">
                    <FaChevronLeft />
                  </button>
                  <button className="pd-slider-btn pd-slider-next" onClick={handleNextImage} aria-label="Next Image">
                    <FaChevronRight />
                  </button>
                  <div className="pd-dots">
                    {images.map((_, i) => (
                      <span
                        key={i}
                        className={`pd-dot ${i === mainIdx ? 'active' : ''}`}
                        onClick={(e) => { e.stopPropagation(); setMainIdx(i); }}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* ── RIGHT: Product Info ── */}
        <div className="pd-info-col">

          <div className="pd-category-tag">{product.category}</div>

          <div className="pd-title-row">
            <h1 className="pd-title">{product.name}</h1>
            <button className="pd-share-btn" onClick={handleShare} title="Share" aria-label="Share">
              <FaShareAlt />
            </button>
          </div>

          {/* Price Section */}
          {product.price != null && (
            <div className="pd-price-section">
              {isLoggedIn ? (
                <>
                  <span className="pd-price-current">
                    ₹{product.offer > 0 ? Math.round(product.price * (1 - product.offer / 100)) : product.price}
                  </span>
                  {product.offer > 0 && (
                    <>
                      <span className="pd-price-old">₹{product.price}</span>
                      <span className="pd-price-offer">{product.offer}% off</span>
                    </>
                  )}
                </>
              ) : (
                <div className="pd-price-locked">
                  <FaLock size={14} />
                  <span><Link to="/login" className="auth-link">Login</Link> or <Link to="/register" className="auth-link">Register</Link> to see pricing</span>
                </div>
              )}
            </div>
          )}

          {product.description && (
            <div className="pd-description">
              {product.shortDescription && <p className="pd-short-desc">{product.shortDescription}</p>}
              {product.description}
            </div>
          )}

          {/* Specs */}
          <h4 className="pd-specs-heading">Product Details</h4>
          <div className="pd-specs-table">
            {product.sku && (
              <div className="pd-spec-row">
                <span className="pd-spec-label">SKU</span>
                <span className="pd-spec-value">{product.sku}</span>
              </div>
            )}
            {product.size && (
              <div className="pd-spec-row">
                <span className="pd-spec-label">Dimensions</span>
                <span className="pd-spec-value">{product.size}</span>
              </div>
            )}
            {product.material && (
              <div className="pd-spec-row">
                <span className="pd-spec-label">Material</span>
                <span className="pd-spec-value">{product.material}</span>
              </div>
            )}
            <div className="pd-spec-row">
              <span className="pd-spec-label">Availability</span>
              <span className={`pd-spec-value ${product.stock !== false ? 'pd-in-stock' : 'pd-out-stock'}`}>
                {product.stock !== false ? '✓ In Stock' : '✗ Out of Stock'}
              </span>
            </div>
          </div>

          {/* Quantity + Order Section — Only for approved users */}
          {isApproved && (
            <div className="pd-order-section">
              <div className="pd-quantity-row">
                <span className="pd-quantity-label">Quantity:</span>
                <div className="pd-quantity-control">
                  <button className="pd-qty-btn" onClick={() => setQuantity(q => Math.max(1, q - 1))}>
                    <FaMinus size={12} />
                  </button>
                  <input
                    type="number"
                    className="pd-qty-input"
                    value={quantity}
                    min={1}
                    onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  />
                  <button className="pd-qty-btn" onClick={() => setQuantity(q => q + 1)}>
                    <FaPlus size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* WhatsApp CTA */}
          <div className="pd-cta">
            <button className="pd-whatsapp-btn" onClick={handleWhatsAppInquiry}>
              <FaWhatsapp size={22} />
              {isApproved ? 'Order via WhatsApp' : 'Inquire via WhatsApp'}
            </button>
          </div>

        </div>
      </div>

      {/* Related Products */}
      {related.length > 0 && (
        <div className="pd-related">
          <h3 className="pd-related-heading">You Might Also Like</h3>
          <div className="product-grid">
            {related.map((r) => <ProductCard key={r.id} product={r} />)}
          </div>
        </div>
      )}

      {/* Gallery Modal */}
      <Modal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        centered
        size="xl"
        className="gallery-modal-dark"
        backdropClassName="gallery-backdrop-dark"
        fullscreen={true}
      >
        <Modal.Body className="p-0 gallery-modal-body-dark">
          <div className="gallery-header">
            <div className="gallery-header-left">
              <h5 className="gallery-title">{product.name}</h5>
              <span className="gallery-subtitle">Click to zoom image</span>
            </div>
            <div className="gallery-header-right">
              <a href={images[modalMainIdx]} download className="action-btn-dark" title="Download">
                <FaDownload />
              </a>
              <button onClick={() => setShowGalleryModal(false)} className="gallery-close-btn-dark">
                <FaTimes />
              </button>
            </div>
          </div>

          <div className="gallery-main-view-dark">
            {images.length > 1 && (
              <button onClick={prevModalImage} className="mobile-nav-btn prev"><FaChevronLeft /></button>
            )}
            <div className="gallery-image-wrapper">
              {isVideoUrl(images[modalMainIdx])
                ? <video src={images[modalMainIdx]} controls autoPlay style={{ width: '100%', height: '100%', maxHeight: '85vh', objectFit: 'contain' }} />
                : <ZoomableImage src={images[modalMainIdx]} alt="Full View" />
              }
            </div>
            {images.length > 1 && (
              <button onClick={nextModalImage} className="mobile-nav-btn next"><FaChevronRight /></button>
            )}
          </div>

          <div className="gallery-thumbs-track-dark">
            {images.map((src, i) => (
              <div
                key={i}
                onClick={() => setModalMainIdx(i)}
                className={`gallery-modal-thumb-dark ${i === modalMainIdx ? 'active' : ''}`}
              >
                {isVideoUrl(src)
                  ? <video src={src} muted style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  : <img src={src} alt={`Thumb ${i}`} />
                }
              </div>
            ))}
          </div>
        </Modal.Body>
      </Modal>

    </div>
  );
}
